import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  FileText, 
  BookOpen, 
  Presentation, 
  File, 
  Download, 
  ExternalLink, 
  MonitorPlay, 
  PlayCircle, 
  Image, 
  Archive,
  Globe,
  ChevronDown,
  ChevronRight,
  HelpCircle
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ManualDialog } from '@/components/ManualDialog';
import { useNavigate } from 'react-router-dom';

interface BusinessMaterial {
  id: number;
  title: string;
  description?: string;
  url: string;
  material_type: 'manual' | 'presentation' | 'document' | 'pdf';
  category?: string;
  icon_name: string;
  is_active: boolean;
  sort_order: number;
  file_size?: number;
  language: string;
}

const iconMap: Record<string, React.ComponentType<any>> = {
  FileText,
  BookOpen,
  Presentation,
  File,
  Download,
  ExternalLink,
  MonitorPlay,
  PlayCircle,
  Image,
  Archive
};

interface ManualContent {
  id: number;
  title: string;
  content: string;
  version: string;
  language: string;
  updated_at: string;
}

export function BusinessMaterialsCard() {
  const { i18n, t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  
  // Fetch business materials for current language
  const { data: materialsData, isLoading: materialsLoading } = useQuery({
    queryKey: ['business-materials', i18n.language],
    queryFn: async () => {
      // Normalizace jazyka - cs-CZ -> cs, en-US -> en
      const currentLanguage = (i18n.language || 'cs').split('-')[0];
      const response = await fetch(`/api/business-materials?language=${currentLanguage}`);
      if (!response.ok) throw new Error('Failed to fetch materials');
      return response.json();
    },
    refetchInterval: 60000, // Refresh every minute
  });

  // Fetch all available manuals
  const { data: manualsData, isLoading: manualsLoading } = useQuery({
    queryKey: ['manuals-all'],
    queryFn: async () => {
      const response = await fetch('/api/manuals');
      if (!response.ok) throw new Error('Failed to fetch manuals');
      return response.json();
    },
    refetchInterval: 60000, // Refresh every minute
  });

  const materials: BusinessMaterial[] = materialsData?.data || [];
  const allManuals: ManualContent[] = manualsData?.data || [];

  // Group materials by type
  const manuals = materials.filter(m => m.material_type === 'manual');
  const otherMaterials = materials.filter(m => m.material_type !== 'manual');

  const isLoading = materialsLoading || manualsLoading;

  const getIcon = (iconName: string) => {
    const Icon = iconMap[iconName] || FileText;
    return <Icon className="h-4 w-4" />;
  };

  const handleMaterialClick = (material: BusinessMaterial) => {
    // Open external link (internal references are handled by ManualDialog wrapper)
    window.open(material.url, '_blank');
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    const mb = bytes / (1024 * 1024);
    return mb < 1 ? ` (${Math.round(bytes / 1024)} KB)` : ` (${mb.toFixed(1)} MB)`;
  };

  const renderMaterialButton = (material: BusinessMaterial) => {
    const buttonContent = (
      <div className="flex items-start gap-3 w-full pr-2">
        <div className="flex-shrink-0 mt-0.5">
          {getIcon(material.icon_name)}
        </div>
        <div className="flex-1 text-left min-w-0">
          <div className="font-medium break-words">
            {material.title}
            {formatFileSize(material.file_size)}
          </div>
          {material.description && (
            <div className="text-xs text-muted-foreground mt-1 break-words">
              {material.description}
            </div>
          )}
          {material.category && (
            <div className="mt-2">
              <Badge variant="secondary" className="text-xs">
                {material.category}
              </Badge>
            </div>
          )}
        </div>
      </div>
    );

    // For internal manual, wrap with ManualDialog
    if (material.url === '#internal-manual') {
      return (
        <ManualDialog key={material.id}>
          <Button
            variant="outline"
            className="w-full justify-start h-auto p-3"
          >
            {buttonContent}
          </Button>
        </ManualDialog>
      );
    }

    // For other materials, regular button with click handler
    return (
      <Button
        key={material.id}
        variant="outline"
        className="w-full justify-start h-auto p-3"
        onClick={() => handleMaterialClick(material)}
      >
        {buttonContent}
      </Button>
    );
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Obchodní materiály
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-2"></div>
            <p className="text-sm text-muted-foreground">Načítám materiály...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const hasContent = allManuals.length > 0 || materials.length > 0;

  return (
    <Card>
      <CardHeader 
        className="cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Obchodní materiály
            {hasContent && (
              <Badge variant="outline" className="text-xs ml-2">
                {allManuals.length + materials.length}
              </Badge>
            )}
          </div>
          <div className="flex-shrink-0">
            {isOpen ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </div>
        </CardTitle>
        <CardDescription>
          Přístup k obchodním materiálům, prezentacím a manuálům
        </CardDescription>
      </CardHeader>
      
      <div className={`transition-all duration-200 ease-in-out overflow-hidden ${
        isOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
      }`}>
        <CardContent>
            {!hasContent ? (
              <div className="text-center py-4 text-muted-foreground">
                <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Žádné materiály nejsou k dispozici</p>
              </div>
            ) : (
              <div className="space-y-4">
            {/* Obchodní manuály z databáze */}
            {allManuals.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <BookOpen className="h-4 w-4 text-primary" />
                  <h4 className="font-medium text-sm">{t('dashboard.internal_manual') || 'Obchodní manuál'}</h4>
                </div>
                <div className="space-y-2">
                  {allManuals.map((manual) => (
                    <ManualDialog key={manual.id} manualLanguage={manual.language}>
                      <Button
                        variant="outline"
                        className="w-full justify-start h-auto p-3 overflow-hidden"
                      >
                        <div className="flex items-start gap-3 w-full overflow-hidden">
                          <div className="flex-shrink-0 mt-0.5">
                            <BookOpen className="h-4 w-4" />
                          </div>
                          <div className="flex-1 text-left min-w-0 overflow-hidden">
                            <div className="font-medium break-words" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                              {manual.title}
                            </div>
                            <div className="text-xs text-muted-foreground mt-1 break-words" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                              {t('manual_editor.version')} {manual.version} • {t('manual_editor.last_updated')}: {new Date(manual.updated_at).toLocaleDateString()}
                            </div>
                            <div className="mt-2">
                              <Badge variant="secondary" className="text-xs">
                                <Globe className="h-3 w-3 mr-1" />
                                {manual.language === 'cs' ? '🇨🇿 Čeština' : '🇺🇸 English'}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </Button>
                    </ManualDialog>
                  ))}
                </div>
              </div>
            )}
            
            {/* Ostatní manuály z business_materials */}
            {manuals.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="h-4 w-4 text-primary" />
                  <h4 className="font-medium text-sm">Další manuály</h4>
                </div>
                <div className="space-y-2">
                  {manuals.map((material) => renderMaterialButton(material))}
                </div>
              </div>
            )}

            {/* FAQ tlačítko */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <HelpCircle className="h-4 w-4 text-primary" />
                <h4 className="font-medium text-sm">Často kladené otázky (FAQ)</h4>
              </div>
              <Button
                variant="outline"
                className="w-full justify-start h-auto p-4"
                onClick={() => navigate('/faq')}
              >
                <div className="flex items-start gap-3 w-full">
                  <div className="flex-shrink-0 mt-0.5">
                    <HelpCircle className="h-4 w-4" />
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <div className="font-medium break-words">
                      FAQ pro obchodníky
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 break-words" style={{ wordBreak: 'break-word', overflowWrap: 'break-word', hyphens: 'auto' }}>
                      Otázky o kamnech a obchodních podmínkách
                    </div>
                    <div className="mt-2">
                      <Badge variant="secondary" className="text-xs">
                        16 otázek
                      </Badge>
                    </div>
                  </div>
                  <div className="flex-shrink-0 mt-1">
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
              </Button>
            </div>

            {/* Separator between FAQ and other materials */}
            {otherMaterials.length > 0 && (
              <Separator />
            )}

            {/* Ostatní materiály */}
            {otherMaterials.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Presentation className="h-4 w-4 text-primary" />
                  <h4 className="font-medium text-sm">Prezentace a dokumenty</h4>
                </div>
                <div className="space-y-2">
                  {otherMaterials.map((material) => {
                    const buttonContent = (
                      <div className="flex items-start gap-3 w-full pr-2">
                        <div className="flex-shrink-0 mt-0.5">
                          {getIcon(material.icon_name)}
                        </div>
                        <div className="flex-1 text-left min-w-0">
                          <div className="font-medium break-words">
                            {material.title}
                            {formatFileSize(material.file_size)}
                          </div>
                          {material.description && (
                            <div className="text-xs text-muted-foreground mt-1 break-words">
                              {material.description}
                            </div>
                          )}
                          <div className="flex flex-wrap gap-2 mt-2">
                            {material.category && (
                              <Badge variant="secondary" className="text-xs">
                                {material.category}
                              </Badge>
                            )}
                            <Badge variant="outline" className="text-xs">
                              {material.material_type === 'presentation' ? 'Prezentace' :
                               material.material_type === 'pdf' ? 'PDF' :
                               material.material_type === 'document' ? 'Dokument' : 
                               material.material_type}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    );

                    // For internal manual, wrap with ManualDialog
                    if (material.url === '#internal-manual') {
                      return (
                        <ManualDialog key={material.id}>
                          <Button
                            variant="outline"
                            className="w-full justify-start h-auto p-3"
                          >
                            {buttonContent}
                          </Button>
                        </ManualDialog>
                      );
                    }

                    // For other materials, regular button with click handler
                    return (
                      <Button
                        key={material.id}
                        variant="outline"
                        className="w-full justify-start h-auto p-3"
                        onClick={() => handleMaterialClick(material)}
                      >
                        {buttonContent}
                      </Button>
                    );
                  })}
                </div>
              </div>
            )}
              </div>
            )}
        </CardContent>
      </div>
    </Card>
  );
}